import moment, { Moment } from 'moment';
import { Holiday } from 'api/holiday-calendar/types';
import { DateType, User } from 'api/models';
import { TimeoffResponse } from 'api/timeoff/types';
import { getCapacityInMinutesForADay } from 'components/organisms/user-capacity/utils';
import {
  isNonWorkingDay,
  type WorkingDaysConfiguration,
} from 'utils/working-days';
import { checkIfCurrentDateHasTimeOff } from './time-off-utils';

export const checkIfCurrentDateHasHoliday = (
  holidays?: Holiday[],
  date = moment(),
): Holiday | undefined => {
  if (holidays == null || holidays.length === 0) {
    return undefined;
  }

  return holidays.find((holiday) =>
    date.isBetween(
      moment(holiday.holidayStart).startOf('day'),
      moment(holiday.holidayEnd).endOf('day'),
      undefined,
      '[]',
    ),
  );
};

export const getHolidayArray = (
  startDate: Moment | undefined,
  endDate: Moment,
  holidayArray: Holiday[] | undefined,
): Holiday[] | undefined => {
  if (startDate == null || endDate == null) {
    return undefined;
  }

  if (!holidayArray) {
    return undefined;
  }

  return holidayArray
    ?.filter(
      (holiday) =>
        moment(holiday.holidayStart).isBetween(
          startDate.startOf('day'),
          endDate.endOf('day'),
          undefined,
          '[]',
        ) ||
        moment(holiday.holidayEnd).isBetween(
          startDate.startOf('day'),
          endDate.endOf('day'),
          undefined,
          '[]',
        ) ||
        moment(startDate).isBetween(
          moment(holiday.holidayStart).startOf('day'),
          moment(holiday.holidayEnd).endOf('day'),
          undefined,
          '[]',
        ) ||
        moment(endDate).isBetween(
          moment(holiday.holidayStart).startOf('day'),
          moment(holiday.holidayEnd).endOf('day'),
          undefined,
          '[]',
        ),
    )
    .sort((a, b) => {
      return moment.utc(a.holidayStart).diff(moment.utc(b.holidayEnd));
    });
};

export const checkIfHolidayInBetween = (
  range: [DateType, DateType],
  holidaysValue: Holiday[],
): boolean => {
  if (range[0] !== null && range[1] !== null) {
    if (range[0] !== range[1]) {
      const holidayArray =
        getHolidayArray(moment(range[0]), moment(range[1]), holidaysValue) ||
        [];

      if (holidayArray.length > 0) {
        return true;
      }
    }
  }

  return false;
};

export const getNoOfHolidayInBetween = (
  range: [DateType, DateType],
  holidaysValue?: Holiday[],
): number => {
  let holidayDaysCount = 0;

  if (range[0] !== null && range[1] !== null && holidaysValue) {
    if (range[0] !== range[1]) {
      const holidayArray =
        getHolidayArray(moment(range[0]), moment(range[1]), holidaysValue) ||
        [];

      if (holidayArray.length > 0) {
        holidayArray.forEach((holiday) => {
          const startDate = moment(holiday.holidayStart, 'YYYY-MM-DD');
          const endDate = moment(holiday.holidayEnd, 'YYYY-MM-DD');

          holidayDaysCount += endDate.diff(startDate, 'days') + 1;
        });
      }
    }
  }

  return holidayDaysCount;
};

export const getNoOfDaysOfTimeOffAndHolidayInWeek = (
  startDate: Moment,
  noOfWorkingDaysInAWeek: number,
  userTimeOffDetails?: TimeoffResponse[],
  holidays?: Holiday[],
): number => {
  let noOfDays = 0;

  [...Array(noOfWorkingDaysInAWeek)].forEach((_, index) => {
    const currentDay = startDate.add(index, 'day');
    let isHoliday;

    const isTimeOff = checkIfCurrentDateHasTimeOff(
      userTimeOffDetails,
      currentDay,
    );

    if (!isTimeOff) {
      isHoliday = checkIfCurrentDateHasHoliday(holidays, currentDay);
    }
    noOfDays += isTimeOff || isHoliday ? 1 : 0;
  });

  return noOfDays;
};

export const getMinutesOfTimeOffAndHolidayInWeek = (
  startDate: Moment,
  user: User,
  workingDaysConfiguration: WorkingDaysConfiguration,
  noOfWorkingDaysInAWeek: number,
  holidays?: Holiday[],
): {
  totalTimeOffHolidayMinutes: number;
  totalTimeOffMinutes: number;
} => {
  let totalTimeOffHolidayMinutes = 0;
  let totalTimeOffMinutes = 0;

  [...Array(noOfWorkingDaysInAWeek)].forEach((value, index) => {
    const currentDay = startDate.clone().add(index, 'days');

    // Check if the current day is a non-working day or a holiday
    const isNonWorkingDayFlag = isNonWorkingDay(
      currentDay,
      workingDaysConfiguration,
    );
    const isHoliday = checkIfCurrentDateHasHoliday(holidays, currentDay);

    if (!isNonWorkingDayFlag && isHoliday) {
      // If it's a holiday, add the full daily capacity to holiday minutes
      totalTimeOffHolidayMinutes += getCapacityInMinutesForADay(
        user.capacityMinutes,
        workingDaysConfiguration,
      );
    } else if (!isNonWorkingDayFlag) {
      // If it's not a holiday, check for time off
      const isTimeOff = checkIfCurrentDateHasTimeOff(
        user?.userTimeOffDetails,
        currentDay,
      );

      if (isTimeOff) {
        // Add the time off duration to both total time off minutes and holiday minutes
        const timeOffDuration = isTimeOff?.timeOffDuration ?? 0;

        totalTimeOffMinutes += timeOffDuration;
        totalTimeOffHolidayMinutes += timeOffDuration;
      }
    }
  });

  return {
    totalTimeOffHolidayMinutes,
    totalTimeOffMinutes,
  };
};
